import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import validator from "validator";
import AuthTerm from "../../../components/AuthTerm/authTerm";
import CreatorQuote from "../../../components/CreatorQuote/creatorQuote";
import ErrorMsg from "../../../components/ErrorMsg/errorMsg";
import { loginEmail } from "../../../Utils/SupabaseServices/UserDatabase";
import { supabase } from "../../../config/supabaseClient";
import "./EmailLogin.css";

import track, { TRACKING_TYPES } from "../../../Utils/SupabaseServices/Track";
import { getUser } from "../../../Utils/HelperFunction/HelperFunction";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<any>("");
  const [emailError, setEmailError] = useState<any>("");
  const [usernameError, setUsernameError] = useState<any>("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth <= 768);
		};
		window.addEventListener('resize', handleResize);
	}, []);
  
  useEffect(() => {
    track(location.pathname, TRACKING_TYPES.PAGE, {
      pathname: location.pathname,
      user_id: "",
    });
  }, []);

  async function signInWithEmail(e: any) {
    e.preventDefault();
    setError("");

    try {
      // First authenticate the user
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error("Authentication error:", error);
        setError("Invalid login credentials. Please try again.");
        return;
      }

      // Then check if the user is an admin
      const { data: users, error: userError } = await supabase
        .from("user")
        .select("*")
        .eq("team_admin", email);


      if (userError) {
        console.error("Admin verification error:", userError);
        setError("Error verifying admin status. Please contact support.");
        return;
      }

      if (!users || users.length === 0) {
        setError("You don't have admin privileges to access this portal.");
        return;
      }

      // Mark as authenticated to show username input
      setIsAuthenticated(true);
      setError("");
    } catch (err: any) {
      console.error("Login error:", err);
      setError("An error occurred during login: " + err.message);
    }
  }

  const validateEmail = (email: any) => {
    if (validator.isEmail(email)) {
      setEmailError("");
      return true;
    } else {
      setEmailError("Enter valid Email!");
      return false;
    }
  };

  function validateCredentials(e: any) {
    e.preventDefault();
    const isEmailValid = validateEmail(email);

    if (isEmailValid && password) {
      signInWithEmail(e);
    } else if (!password) {
      setError("Password is required");
    }
  }

  function LoginButton() {
    return (
      <button
        className="submit-btn"
        disabled={!email || !password}
        style={{ opacity: !email || !password ? 0.7 : 1 }}
      >
        Sign in
      </button>
    );
  }

  async function getUserAccess(e: any) {
    if (e) e.preventDefault();
    
    // First check if username is provided
    if (!username.trim()) {
      setUsernameError("Please enter a username to access");
      return;
    }

    try {
      const { data: users, error: userError }:any = await supabase
        .from("user")
        .select("*")
        .eq("team_admin", email)
        .eq("username", username);

      
      if (!users || users.length === 0) {
        setError("You don't have admin privileges to access this username.");
        return;
      }
      
      // Store the admin data only after we have both email and username verified
      localStorage.setItem("loggedIn", username);
      localStorage.setItem("admin", JSON.stringify(users[0]));
      // Dispatch event only after all data is properly set
      window.dispatchEvent(new Event('adminLogin'));
      
      // Navigate to the user's dashboard
      navigate(`/dashboard/${username}`);
    } catch (err: any) {
      console.error("Navigation error:", err);
      setError("An error occurred while accessing the dashboard: " + err.message);
    }
  }

  function AccessButton() {
    return (
      <button
        type="submit"
        className="submit-btn"
        disabled={!username}
        style={{ opacity: !username ? 0.7 : 1 }}
      >
        Access Dashboard
      </button>
    );
  }

  return (
    <div className="auth-container">
      <div className="login-auth">
        <div className="auth-phone-signup-container-flex">
          <div className="navbar_container">
            <a
              href="/"
              className="navbar_logo-link w-nav-brand"
              aria-label="home"
            >
              <div className="logo-text"
              >
                <img
                  className="homepage-icon-logo"
                  // src="/assets/output-onlinepngtools-edited.png"
                  // src="/assets/NewLogo-removebg.png"
                  src={isMobile ? "/assets/Logo-White.svg" : "/assets/Wordmark+Logo-White.svg"}
                  alt="search bubble logo"
                />
              </div>
            </a>
            <nav className="navbar_menu w-nav-menu">
              <div className="navbar_menu-link-wrapper">
                <a href="/subscribe" className="navbar_link w-nav-link sign-up">
                  sign up for free
                </a>
              </div>
            </nav>
          </div>

          <div className="auth-form-wrapper">
            <div className="auth-title-div">
              <p className="auth-title-div-p">Admin Login</p>
            </div>

            {!isAuthenticated ? (
              // Step 1: Email/Password Authentication Form
              <form className="input-form" onSubmit={validateCredentials}>
                <div className="input-div email">
                  <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e: any) => setEmail(e.target.value)}
                    required
                  />
                </div>
                {emailError ? <ErrorMsg error={emailError} /> : ""}

                <div className="input-div">
                  <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e: any) => setPassword(e.target.value)}
                    required
                  />
                </div>
                {error ? <ErrorMsg error={error} /> : ""}
                <LoginButton />
              </form>
            ) : (
              // Step 2: Username Input Form
              <form className="input-form" onSubmit={getUserAccess}>
                <div
                  className="auth-success-message"
                  style={{
                    color: "#10b981",
                    marginBottom: "20px",
                    backgroundColor: "rgba(16, 185, 129, 0.1)",
                    padding: "10px",
                    borderRadius: "4px",
                  }}
                >
                  Authentication successful! Enter a username to access their
                  dashboard.
                </div>
                <div className="input-div username">
                  <input
                    type="text"
                    placeholder="Username to access"
                    value={username}
                    onChange={(e: any) => {
                      setUsername(e.target.value);
                      setUsernameError("");
                    }}
                    required
                  />
                </div>
                {usernameError ? <ErrorMsg error={usernameError} /> : ""}
                {error ? <ErrorMsg error={error} /> : ""}
                <AccessButton />
              </form>
            )}
          </div>
          <AuthTerm />
        </div>
        <CreatorQuote />
      </div>
    </div>
  );
};

export default AdminLogin;